import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wand2, ImageIcon, Radar, Sparkles, ArrowRight, X, MapPin, Palette, Rocket, Trophy } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

const KEY = "tikboost-onboarding-v2-done";

/**
 * OnboardingModal — 6-step personalisation wizard.
 * Rebuilt from scratch to fix a Chrome/desktop bug where Radix Select
 * inside a backdrop-blur modal would freeze the entire dialog. This
 * version uses native <select> for perfect reliability on every browser
 * and every desktop OS.
 */

const COUNTRIES = [
  { v: "global", flag: "🌍", label: "Global" },
  { v: "us", flag: "🇺🇸", label: "United States" },
  { v: "gb", flag: "🇬🇧", label: "United Kingdom" },
  { v: "in", flag: "🇮🇳", label: "India" },
  { v: "ca", flag: "🇨🇦", label: "Canada" },
  { v: "au", flag: "🇦🇺", label: "Australia" },
  { v: "de", flag: "🇩🇪", label: "Germany" },
  { v: "fr", flag: "🇫🇷", label: "France" },
  { v: "es", flag: "🇪🇸", label: "Spain" },
  { v: "br", flag: "🇧🇷", label: "Brazil" },
  { v: "mx", flag: "🇲🇽", label: "Mexico" },
  { v: "jp", flag: "🇯🇵", label: "Japan" },
  { v: "kr", flag: "🇰🇷", label: "South Korea" },
  { v: "sa", flag: "🇸🇦", label: "Saudi Arabia" },
  { v: "ae", flag: "🇦🇪", label: "UAE" },
];

const NICHES = [
  "I'll decide later", "Beauty", "Fitness", "Business", "Tech", "Gaming", "Cooking",
  "Travel", "Lifestyle", "Fashion", "Comedy", "Education", "Finance", "Music",
];

export default function OnboardingModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [country, setCountry] = useState("global");
  const [niche, setNiche] = useState("I'll decide later");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(KEY)) return;
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/settings", {
        country,
        niche,
      });
      await refresh();
    } catch {
      // Silently degrade — onboarding is optional data.
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const STEPS = [
    {
      icon: Wand2,
      accent: "from-cyan-400 to-blue-500",
      title: "Welcome to TikBoost AI",
      body: "A calm workspace for a loud algorithm. Everything you need to go from a spark to a shipped video lives here.",
    },
    {
      icon: MapPin,
      accent: "from-cyan-400 to-pink-500",
      title: "Let's personalise your workspace",
      body: "Two questions — takes about 5 seconds. We'll pre-fill Trend Radar and Script Studio so your first result actually fits you.",
      render: () => (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ob-country" className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              <MapPin className="w-3 h-3" /> Country
            </label>
            <div className="relative mt-2">
              <select
                id="ob-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full h-11 rounded-lg border border-input bg-background pl-3 pr-9 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-400/60 hover:border-cyan-500/40 transition-colors"
                data-testid="ob-country"
              >
                {COUNTRIES.map((c) => <option key={c.v} value={c.v}>{c.flag}  {c.label}</option>)}
              </select>
              <ArrowRight className="w-3 h-3 absolute right-3 top-1/2 -translate-y-1/2 -rotate-90 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div>
            <label htmlFor="ob-niche" className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              <Sparkles className="w-3 h-3" /> Niche
            </label>
            <div className="relative mt-2">
              <select
                id="ob-niche"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                className="w-full h-11 rounded-lg border border-input bg-background pl-3 pr-9 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-400/60 hover:border-cyan-500/40 transition-colors"
                data-testid="ob-niche"
              >
                {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <ArrowRight className="w-3 h-3 absolute right-3 top-1/2 -translate-y-1/2 -rotate-90 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: Wand2,
      accent: "from-cyan-400 to-pink-500",
      title: "Script Studio",
      body: "Turn any idea into a complete viral script kit — hook, scene breakdown, b-roll, camera angles, hashtags, viral score.",
      cta: { to: "/dashboard/generator", label: "Open Script Studio" },
    },
    {
      icon: ImageIcon,
      accent: "from-pink-500 to-fuchsia-500",
      title: "Image Studio · New",
      body: "Generate scroll-stopping images for any platform in seconds. Studio-grade presets, negative prompts, brand colours and safe mode built in.",
      cta: { to: "/dashboard/images", label: "Open Image Studio" },
    },
    {
      icon: Radar,
      accent: "from-pink-500 to-amber-400",
      title: "Trend Radar",
      body: "Fresh trends filtered by country, language and niche. One click sends any trend straight into Script Studio.",
      cta: { to: "/dashboard/trends", label: "Open Trend Radar" },
    },
    {
      icon: Trophy,
      accent: "from-cyan-400 to-emerald-400",
      title: "Weekly Creator Rewards",
      body: "Ship consistently and earn bonus AI credits, streak badges and weekly quests. Get started with your first generation to warm up your streak.",
      cta: { to: "/dashboard", label: "Start creating" },
    },
  ];
  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;
  const Icon = step.icon;
  const isPrefStep = idx === 1;

  const advance = async () => {
    if (isPrefStep) {
      await savePreferences();
    }
    if (isLast) close(); else setIdx((i) => i + 1);
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4 bg-background/70 backdrop-blur-xl"
      data-testid="onboarding-modal"
      role="dialog"
      aria-labelledby="onboarding-title"
      aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 relative overflow-hidden"
      >
        <div className="aurora opacity-25 pointer-events-none" />
        <button
          type="button"
          onClick={close}
          aria-label="Skip onboarding"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
          data-testid="onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${step.accent} grid place-items-center`}>
            <Icon className="w-6 h-6 text-zinc-950" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
              className="mt-6"
            >
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">
                Step {idx + 1} of {STEPS.length}
              </p>
              <h2 id="onboarding-title" className="mt-2 font-display text-2xl sm:text-3xl">{step.title}</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">{step.body}</p>
              {step.render ? step.render() : null}
            </motion.div>
          </AnimatePresence>

          {/* Progress bar */}
          <div className="mt-8 h-1 rounded-full bg-border overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 to-pink-500 transition-all duration-500"
              style={{ width: `${((idx + 1) / STEPS.length) * 100}%` }}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={close}
              className="rounded-full"
              data-testid="onboarding-skip"
            >
              Skip
            </Button>
            {step.cta && (
              <Button asChild variant="secondary" className="rounded-full" onClick={close} data-testid="onboarding-cta">
                <Link to={step.cta.to}><Sparkles className="w-4 h-4 mr-2" /> {step.cta.label}</Link>
              </Button>
            )}
            <Button
              type="button"
              disabled={saving}
              onClick={advance}
              className="rounded-full ml-auto bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 border-0 hover:brightness-110"
              data-testid="onboarding-next"
            >
              {saving ? "Saving…" : (isPrefStep ? "Save & continue" : (isLast ? "Get started" : "Next"))}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
